import React from 'react'
import {
  Card,
  Container,
  Element,
  Form,
  Heading,
  Level,
  Loader,
  Section,
  Tag,
} from 'react-bulma-components'
import Layout from '../../components/layout/layout'
import { useNavigate } from 'react-router-dom'
import useUrlId from '../../hooks/use-url-id'
import { useMissionQuery } from '../../queries/missions'
import MissionMessages from '../../components/missions/mission-messages'
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs'
import MissionActions from '../../components/missions/mission-actions'
import MissionPageTitle, { EditActions } from '../../components/missions/titles/mission-page-title'
import {
  dateInputValuesToWeekDates,
  dateToDateInputValue,
  localDate,
  startOfWeek,
} from '../../utils/date'
import Protected from '../../components/protected/protected'
import { Link } from 'react-router-dom'
import { sortBy } from 'lodash'
import { useWorkersRequestsQuery } from '../../queries/workers-requests'
import History from '../../components/history/history'
import locales from '../../../../locales/missions'
import { BetterTable } from 'src/components/better-table'
import { useWorkPeriodsList } from 'src/components/work-periods/list'
import { useWorkPeriodsQuery } from 'src/queries/work-periods'
import { getDocIdAsString } from '../../utils/documents'
import FieldColumn from '../../components/filters/field-column'
import { IsoDate } from '../../hooks/use-calendar'

const MissionDetails: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { id, isNew } = useUrlId()
  const { data: mission } = useMissionQuery(id)
  const isInterimAgencyMission = Boolean(mission !== undefined && 'interimAgency' in mission)
  const { data: workersRequest } = useWorkersRequestsQuery(
    { 'posts.missions': mission?._id },
    { enabled: Boolean(isInterimAgencyMission) },
  )
  const workPeriodsDefinition = useWorkPeriodsList()

  /** Selected week for the work periods list (IsoDate = any day in the week). Defaults to first week of mission. */
  const [selectedWeekIso, setSelectedWeekIso] = React.useState<IsoDate | ''>('')

  const linkedWorkersRequestId = workersRequest?.[0]?._id

  const effectiveWeekIso: IsoDate | undefined = mission
    ? selectedWeekIso || dateToDateInputValue(startOfWeek(mission.start))
    : undefined
  const weekRange = effectiveWeekIso ? dateInputValuesToWeekDates(effectiveWeekIso) : null

  const { data: workPeriods } = useWorkPeriodsQuery(
    {
      mission: id,
      summary: true,
      ...(weekRange ? { start: weekRange.start, end: weekRange.end } : {}),
    },
    { enabled: Boolean(id) && Boolean(mission) },
  )

  const breadcrumbs = useBreadcrumbs(id)
  React.useEffect(() => {
    if (isNew) navigate('/') // TODO: TEMP quickfix to prevent going to the mission creation page
  }, [isNew])

  const historyItems = React.useMemo(() => {
    return sortBy(mission?.history || [], 'createdAt').reverse()
  }, [mission])

  const hasCancelledEndDate =
    mission?.isCancelled &&
    mission?.history
      ?.find(h => h.action === 'missionCancelled')
      ?.changes?.find(c => c.attribute === 'mission.end')?.oldValue

  if (!mission) return <Loader />

  return (
    <Layout>
      <Section>
        <Container max={true} breakpoint="widescreen">
          <Level>
            <Level.Side>
              <Breadcrumbs items={breadcrumbs} />
            </Level.Side>
            <Level.Side align="right">{!isNew && <EditActions mission={mission} />}</Level.Side>
          </Level>
          <Level alignItems="flex-start" mb={6}>
            <Level.Side>
              <MissionPageTitle
                mission={mission}
                organizationType={isInterimAgencyMission ? 'interimAgency' : 'employer'}
              ></MissionPageTitle>
            </Level.Side>
            <Level.Side align="right">
              <Tag.Group>
                <MissionMessages mission={mission} type="tag" />
              </Tag.Group>
            </Level.Side>
          </Level>
          <Protected roles={['interimAgencyMember', 'employerMember']}>
            <Heading size={4}>Actions</Heading>
            <Card>
              <Card.Content>
                <Element>
                  <MissionActions mission={mission} />
                </Element>
              </Card.Content>
            </Card>
            <Element textAlign={'right'} mt={2}>
              {linkedWorkersRequestId && (
                <Link to={`/workers/${getDocIdAsString(mission.user)}`}>
                  Voir la fiche du travailleur
                </Link>
              )}
            </Element>
          </Protected>
          <Card mt={6}>
            <Card.Content>
              <Heading size={5}>
                {`Du ${localDate(mission.start)} ${mission.end ? `au ${localDate(mission.end)}` : ''}`}
              </Heading>
              <Level mb={4}>
                <Level.Side>
                  <FieldColumn>
                    <Form.Field>
                      <Form.Label>Semaine</Form.Label>
                      <Form.Control>
                        <Form.Input
                          type="date"
                          value={effectiveWeekIso}
                          onChange={e => setSelectedWeekIso((e.target.value || '') as IsoDate)}
                          min={dateToDateInputValue(mission.start)}
                          max={mission.end && dateToDateInputValue(mission.end)}
                          name="week"
                        />
                      </Form.Control>
                    </Form.Field>
                  </FieldColumn>
                </Level.Side>
              </Level>
              <BetterTable
                columns={workPeriodsDefinition}
                data={workPeriods ?? []}
                keys={[
                  'day',
                  'dates',
                  'counted',
                  'events',
                  'isAbandoned',
                  'isValidated',
                  'validateAction',
                ]}
                noDataMessage="Aucune journée de travail pour cette semaine"
              />
              {hasCancelledEndDate && (
                <Element textColor="grey" textAlign={'center'}>
                  Cette mission a été interrompue, elle était initialement prévue jusqu'au{' '}
                  {localDate(hasCancelledEndDate)}
                </Element>
              )}
            </Card.Content>
          </Card>
          <Element textAlign={'right'} mt={2}>
            {linkedWorkersRequestId && (
              <Link to={`/workers-requests/${linkedWorkersRequestId}`}>Voir la demande</Link>
            )}
          </Element>
        </Container>
      </Section>
      <Section>
        <Container max={true} breakpoint="desktop">
          <Heading size={4} mt={5}>
            Historique
          </Heading>
          <History items={historyItems} locales={locales.history} />
        </Container>
      </Section>
    </Layout>
  )
}

export default MissionDetails

const useBreadcrumbs = (missionId?: string) =>
  React.useMemo(
    () => [
      {
        label: 'Liste des missions',
        to: '/missions',
      },
      {
        label: `Mission`,
        to: `/missions/${missionId}`,
      },
    ],
    [missionId],
  )
